import { css, cx } from "@emotion/css"

import Button from "ui/buttons/Button"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { MorePopover } from "components/MorePopover"
import { useI18n } from "providers/I18n"

type ButtonBlockProps<T> = {
  options: T[]
  error?: string
  onOpen: VoidFunction
  dataKey?: string
  name: string
  disabled?: boolean
  className?: string
  hasEndAdornment?: boolean
  buttonStyle?: "default" | "primary"
  slice?: number
}

export function MoreButtonBlock<T extends Record<string, any>>(
  props: ButtonBlockProps<T>,
) {
  const { t } = useI18n()
  const {
    options,
    error,
    onOpen,
    dataKey = "",
    name,
    disabled = false,
    className,
    hasEndAdornment = false,
    buttonStyle = "primary",
    slice = 2,
  } = props
  const optionsMapped = options?.map((o) => ({
    ...o,
    name: o?.name,
  }))
  const optionsToShow = optionsMapped
    ?.slice(0, slice)
    .map((v) =>
      !(v.name && v.name?.length > 10)
        ? v.name
        : v.name?.substring(0, 9) + "...",
    )
    .join(", ")

  return (
    <div
      className={cx(
        styles.buttonContainer,
        {
          [styles.error]: !!error,
          [styles.buttonContainerHover]: !disabled,
        },
        className,
      )}
      onClick={!disabled ? onOpen : undefined}
    >
      {!disabled ? (
        <Button
          className={cx(
            "BodyBody-2BlackRegular",
            styles.textButton,
            styles.buttonHover,
          )}
        >
          {options?.length ? (
            optionsToShow
          ) : (
            <span
              className={cx({ [styles.primary]: buttonStyle === "primary" })}
            >
              {!!name && t(`${name}.${dataKey}.add`)}
            </span>
          )}
        </Button>
      ) : (
        <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
          {options.length ? optionsToShow : null}
        </div>
      )}
      <MorePopover
        data={optionsMapped}
        label={`${name}.${dataKey}`}
        name="name"
        slice={slice}
      />
      {hasEndAdornment ? <DropdownIcon className={styles.dropdown} /> : null}
    </div>
  )
}

const styles = {
  buttonContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    border: 1px solid transparent;
    padding-right: var(--spacing-s);
    cursor: pointer;
    > span {
      margin-right: 4px;
    }
  `,
  buttonContainerHover: css`
    :hover {
      border: 1px solid var(--primary-main);
      border-radius: 4px;
    }
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    text-transform: capitalize;
    transition: none;
    &:focus {
      box-shadow: none;
    }
    &:hover {
      background-color: transparent;
      border: 1px solid transparent;
    }
  `,
  buttonHover: css`
    &:hover {
      border: 1px solid transparent;
    }
  `,
  primary: css`
    color: var(--primary-main);
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  readonly: css`
    margin: var(--spacing-m);
  `,
  dropdown: css`
    color: var(--dark-grey);
  `,
}
